import { tryOnScopeDispose, useIntervalFn } from "@vueuse/core"
import { Service } from "dioc"
import { computed, reactive, ref, watch, readonly } from "vue"
import { useStreamStatic } from "~/composables/stream"
import TeamListAdapter from "~/helpers/teams/TeamListAdapter"
import { platform } from "~/platform"
import { min } from "lodash-es"
import { TeamAccessRole } from "~/helpers/backend/graphql"
import { TeamCollectionsService } from "./team-collection.service"
import { DocumentationService } from "./documentation.service"

export const TEAM_COLLECTION_POLL_INTERVAL = 10000

/**
 * Defines a workspace and its information
 */

export type PersonalWorkspace = {
  type: "personal"
}

export type TeamWorkspace = {
  type: "team"
  teamID: string
  teamName: string
  role: TeamAccessRole | null | undefined
}

export type Workspace = PersonalWorkspace | TeamWorkspace

export type WorkspaceServiceEvent = {
  type: "managed-team-list-adapter-polled"
}

/**
 * This services manages workspace related data and actions in Hoppscotch.
 */
export class WorkspaceService extends Service<WorkspaceServiceEvent> {
  public static readonly ID = "WORKSPACE_SERVICE"

  private _currentWorkspace = ref<Workspace>({ type: "personal" })

  /**
   * A readonly reference to the currently selected workspace
   */
  public currentWorkspace = readonly(this._currentWorkspace)

  private teamListAdapterLocks = reactive(new Map<number, number | null>())
  private teamListAdapterLockTicker = 0 // Used to generate unique lock IDs
  private managedTeamListAdapter = new TeamListAdapter(true, false)

  private teamCollectionService = this.bind(TeamCollectionsService)
  private documentationService = this.bind(DocumentationService)

  private currentUser = useStreamStatic(
    platform.auth.getCurrentUserStream(),
    platform.auth.getCurrentUser(),
    () => {
      /* noop */
    }
  )[0]

  private readonly pollingTime = computed(
    () =>
      min(Array.from(this.teamListAdapterLocks.values()).filter((x) => !!x)) ??
      -1
  )

  override onServiceInit() {
    // Dispose the managed team list adapter when the user logs out
    // and initialize it when the user logs in
    watch(
      this.currentUser,
      (user, oldUser) => {
        if (oldUser && user?.uid !== oldUser.uid) {
          if (this.managedTeamListAdapter.isInitialized) {
            this.managedTeamListAdapter.dispose()
          }
          this.changeWorkspace({ type: "personal" })
        }
        if (!user && this.managedTeamListAdapter.isInitialized) {
          this.managedTeamListAdapter.dispose()
        }

        if (user && !this.managedTeamListAdapter.isInitialized) {
          this.managedTeamListAdapter.initialize()
        }
      },
      { immediate: true }
    )

    // Poll the managed team list adapter if the polling time is defined
    const { pause: pauseListPoll, resume: resumeListPoll } = useIntervalFn(
      () => {
        if (this.managedTeamListAdapter.isInitialized) {
          this.managedTeamListAdapter.fetchList()

          this.emit({ type: "managed-team-list-adapter-polled" })
        }
      },
      this.pollingTime,
      { immediate: true }
    )

    // Pause and resume the polling when the polling time changes
    watch(
      this.pollingTime,
      (pollingTime) => {
        if (pollingTime === -1) {
          pauseListPoll()
        } else {
          resumeListPoll()
        }
      },
      { immediate: true }
    )

    // Lambda cannot host the WebSocket connection used by GraphQL
    // subscriptions. Poll the active team over HTTP so remote/import/move
    // changes eventually reconcile even when no local optimistic update ran.
    useIntervalFn(() => {
      const workspace = this._currentWorkspace.value

      if (!this.currentUser.value || workspace.type !== "team") return

      void this.teamCollectionService
        .refreshCollections(workspace.teamID)
        .catch((error) => {
          console.error("Failed to poll team collections:", error)
        })
    }, TEAM_COLLECTION_POLL_INTERVAL)

    // Watch for workspace changes and update team collection service and documentation service accordingly
    this.setupWorkspaceSync()
  }

  /**
   * Sets up synchronization between team collection service and documentation service.
   * Ensures that team collections and published docs stay updated whenever
   * the workspace or user changes.
   *
   * Fixes a bug where the initial fetch failed on cloud instances because
   * authorization was null during user login. Now we wait for authentication
   * to be ready before fetching team collections and published docs.
   */
  private setupWorkspaceSync() {
    watch(
      // Watch identity rather than objects: Vue runs the previous cleanup
      // before the callback, so skipping an equivalent object inside the
      // callback would cancel a pending load without starting its replacement.
      [
        () => {
          const workspace = this._currentWorkspace.value
          return workspace.type === "team" ? workspace.teamID : null
        },
        () => this.currentUser.value?.uid,
      ],
      async ([teamID, userID], _previous, onCleanup) => {
        let cancelled = false
        onCleanup(() => {
          cancelled = true
        })

        // Invalidate pending requests before waiting for the next session.
        this.teamCollectionService.clearCollections()
        this.documentationService.resetWorkspace()
        if (!userID) return

        try {
          await platform.auth.waitProbableLoginToConfirm()
          if (cancelled || this.currentUser.value?.uid !== userID) return

          if (teamID) {
            this.teamCollectionService.changeTeamID(teamID)
            await this.documentationService.fetchTeamPublishedDocs(teamID)
          } else {
            await this.documentationService.fetchUserPublishedDocs()
          }
        } catch (error) {
          console.error("Failed to sync workspace data:", error)
        }
      },
      { immediate: true }
    )
  }

  // TODO: Update this function, its existence is pretty weird
  /**
   * Updates the name of the current workspace if it is a team workspace.
   * @param newTeamName The new name of the team
   */
  public updateWorkspaceTeamName(newTeamName: string) {
    if (this._currentWorkspace.value.type === "team") {
      this._currentWorkspace.value = {
        ...this._currentWorkspace.value,
        teamName: newTeamName,
      }
    }
  }

  /**
   * Changes the current workspace to the given workspace.
   * @param workspace The new workspace
   */
  public changeWorkspace(workspace: Workspace) {
    // Membership administration still uses the real role from TeamListAdapter.
    // Definition controls use this content-scoped role, so all shared
    // workspaces follow the existing viewer/read-only UI path.
    this._currentWorkspace.value =
      workspace.type === "team"
        ? { ...workspace, role: TeamAccessRole.Viewer }
        : workspace
  }

  /**
   * Acquires a team list adapter that is managed by the workspace service.
   * The team list adapter is associated with a Vue Scope and will be disposed
   * when the scope is disposed.
   * @param pollDuration The duration between polls in milliseconds. If null, the team list adapter will not poll.
   */
  public acquireTeamListAdapter(pollDuration: number | null) {
    const lockID = this.teamListAdapterLockTicker++

    this.teamListAdapterLocks.set(lockID, pollDuration)

    tryOnScopeDispose(() => {
      this.teamListAdapterLocks.delete(lockID)
    })

    return this.managedTeamListAdapter
  }
}
